import type { ReactNode } from "react";

import type { CountdownFace } from "../offer-inbox.ts";

/**
 * The hero illustrations, one per designed kind, built from the design's own geometry.
 *
 * All six of the design's kinds are now used. `idle` on the two readiness screens and on `Vehicle`,
 * `radar` on `2 Online`, `countdown` on `3 Offer`, `expired` on `3b`, `earn` on both WP14 earnings
 * screens, `sign` on `8 Proof` and `scan` on `5 Scan` — the last of them arriving once LOG-02
 * published a parcel read, which is what made that screen's subject answerable at all. `4 To
 * pickup` draws none of them, because the design marks it `map:1` and gives it a hero band with
 * nothing in it (Q-68).
 *
 * All of it is CSS and markup, with the only inline value being the countdown's own fraction. That
 * is deliberate: every colour, size and duration lives in a stylesheet where the acceptance check
 * and the token test can both see it, and the one thing that genuinely changes four times a second
 * is passed as a custom property.
 */

/**
 * `box(icon, caption, colour)` in the design: a disc with a glyph, and a caption under it.
 *
 * The design calls this three times with three glyphs, and tints it once. An untinted disc takes
 * `C.card` behind a `C.sub` glyph; the tinted one takes the colour behind white.
 */
export function HeroDisc({
  glyph,
  caption,
  tinted,
  captionIsAbsence,
}: {
  readonly glyph: string;
  readonly caption: string;
  readonly tinted?: boolean;
  /** True when the caption says what could not be found rather than what happened. */
  readonly captionIsAbsence?: boolean;
}): ReactNode {
  return (
    <div className="hero">
      <span
        className={`glyph hero__disc${tinted === true ? " hero__disc--paid" : ""}`}
        aria-hidden="true"
      >
        {glyph}
      </span>
      <span className={`hero__caption${captionIsAbsence === true ? " hero__caption--absent" : ""}`}>
        {caption}
      </span>
    </div>
  );
}

/**
 * `radar`: "Looking for jobs near you".
 *
 * Three rings expanding on a 2.4s cycle staggered by 0.8s, a conic sweep rotating every 2.2s, and
 * the courier at the centre. The animations are in `screens/offers.css` with the design's own
 * timings and its own `prefers-reduced-motion` branch — which stops the rings and holds them at
 * `opacity:.25` rather than hiding them, because three invisible circles is not a still
 * illustration.
 *
 * `aria-hidden` on the whole thing: it is decoration, and what it means — that this connection is
 * waiting for an offer — is in the sheet's title where the design puts it.
 */
export function HeroRadar(): ReactNode {
  return (
    <div className="radar" aria-hidden="true">
      <span className="radar__ring radar__ring--1" />
      <span className="radar__ring radar__ring--2" />
      <span className="radar__ring radar__ring--3" />
      <span className="radar__sweep" />
      <span className="radar__courier glyph">two_wheeler</span>
    </div>
  );
}

/**
 * `countdown`: the ring that runs down, and the one number on this application read in seconds.
 *
 * The ring is a conic gradient whose single stop is the remaining fraction, so `--countdown-fraction`
 * is the only inline style in this application — everything else about it, including the two
 * colours and the beat, is in the stylesheet. Under five seconds the design turns it amber and
 * beats it, which is a class rather than a second inline value.
 *
 * `role="timer"` with `aria-live="off"`: a courier using a screen reader needs the number to be
 * findable and must not have it read aloud four times a second. The label is the full sentence
 * rather than `0:07`, because "seven" on its own says nothing.
 */
export function HeroCountdown({ face }: { readonly face: CountdownFace }): ReactNode {
  return (
    <div className="countdown">
      <div
        className={`countdown__ring${face.urgent ? " countdown__ring--urgent" : ""}`}
        style={{ "--countdown-fraction": face.fraction } as React.CSSProperties}
      >
        <div className="countdown__face">
          <span
            className="countdown__time"
            role="timer"
            aria-live="off"
            aria-label={`${face.label} left to accept`}
          >
            {face.label}
          </span>
          <span className="countdown__label">to accept</span>
        </div>
      </div>
    </div>
  );
}

/**
 * `sign`: `8 Proof`'s hero, which is the recipient's signature — and is therefore empty.
 *
 * The design draws a 210×110 dashed card with the recipient's name written across it in Bricolage
 * Grotesque, italic, rotated six degrees: `Amina K.`, the same name its title prints. That name is
 * the one element `tests/contract/mobile-screen-support.test.ts` says this screen has no source for.
 *
 * So the card is drawn exactly as designed and **nothing is written in it**. Not a placeholder name,
 * not initials, not a scribble: a signature is a person's mark, and a drawn one that nobody made is
 * the single most misleading thing this application could put on a delivery screen. What stands in
 * its place is the design's own `unknown` mark — dashed, mono, carrying a `?` — which is the
 * vocabulary this application already uses for a value the platform did not publish.
 *
 * The real signature cannot be fetched either, and that is worth saying because it looks like it
 * should be: `evidence[].objectRef` names the object, and FIL-02 publishes no read of object bytes
 * on any surface. Its grant is a write grant. Recorded as Q-76.
 */
export function HeroSignature({ because }: { readonly because: string }): ReactNode {
  return (
    <div className="proof__signature">
      <span className="rows__absent" title={because} />
    </div>
  );
}

/**
 * `scan`: `5 Scan`'s hero, which is a barcode in a frame.
 *
 * The design's own geometry, value for value from `art('scan')`: `width:190, height:128,
 * borderRadius:18, border:3px solid #12B76A`, and a barcode made out of a repeating linear gradient
 * in `C.text` at `backgroundSize:'78% 44%'`, centred and not repeated. Every one of those numbers is
 * in `screens/scan.css` where the token test can see it, which is why nothing here is inline.
 *
 * The bars are **not** a real barcode and must not be mistaken for one: the design's gradient is a
 * fixed 10px pattern that encodes nothing, and the thing a courier scanned is a label in their hand
 * rather than anything on this screen. `aria-hidden`, because what it means — that the scan went
 * through — is the chip's word, where the design puts it.
 */
export function HeroScanFrame(): ReactNode {
  return <div className="scan__frame" aria-hidden="true" />;
}
